/**
 * Splash Screen Management
 * Controls the native splash screen for Capacitor apps
 */

import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';

const isNative = Capacitor.isNativePlatform();
let currentHide: Promise<void> | null = null;

/**
 * Hide the splash screen
 * Call this after your app is fully loaded and ready
 */
export const hideSplashScreen = async (fadeOutDuration = 300) => {
  if (!isNative) return;
  if (currentHide) return currentHide;

  currentHide = SplashScreen.hide({
    fadeOutDuration,
  }).catch((error) => {
    console.warn('Failed to hide splash screen:', error);
  }).finally(() => {
    currentHide = null;
  });

  return currentHide;
};

/**
 * Force-clear the native splash during resume recovery.
 * Capacitor hide() is idempotent; repeated calls are safe.
 */
export const forceHideSplashScreen = () => {
  if (!isNative) return;
  void SplashScreen.hide({ fadeOutDuration: 0 }).catch(() => {});
  currentHide = null;
};

/**
 * Show the splash screen
 * Useful for showing during long operations
 */
export const showSplashScreen = async (options?: {
  autoHide?: boolean;
  fadeInDuration?: number;
  fadeOutDuration?: number;
  showDuration?: number;
}) => {
  if (!isNative) return;

  try {
    await SplashScreen.show({
      autoHide: options?.autoHide ?? false,
      fadeInDuration: options?.fadeInDuration ?? 200,
      fadeOutDuration: options?.fadeOutDuration ?? 200,
      showDuration: options?.showDuration ?? 2000,
    });
  } catch (error) {
    console.warn('Failed to show splash screen:', error);
  }
};

export default { hideSplashScreen, forceHideSplashScreen, showSplashScreen };
